import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './QuotationStyles.css';

const API = 'https://cctvapi.shopkwality.in/api';

const QuotationList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [quotations, setQuotations] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchQuotations();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuotations(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchQuotations = async (query = '') => {
    try {
      setLoading(true);
      const endpoint = query 
        ? `${API}/quotations/search?q=${query}`
        : `${API}/quotations`;

      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.success) {
        setQuotations(res.data.data || []);
      } else {
        setQuotations(res.data || []);
      }
    } catch (err) {
      console.error('Fetch quotations error:', err);
      alert('Failed to load quotations');
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteQuotation = async (id) => {
    if (!window.confirm('Are you sure you want to archive this quotation?')) return;

    try {
      await axios.delete(`${API}/quotations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchQuotations(search);
      alert('Quotation deleted successfully');
    } catch (err) {
      alert('Failed to delete quotation');
    }
  };

  const normalizeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'object' && value.d) {
      const asString = value.d.join('') + 'e' + value.e;
      return Number(Number(asString).toFixed(2));
    }
    return Number(value) || 0;
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': '#FFD93D',
      'shared': '#1E90FF',
      'converted': '#2ECC71'
    };
    return colors[status] || '#7F8C8D';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'draft': 'status-draft',
      'shared': 'status-shared',
      'converted': 'status-converted'
    };
    return classes[status] || 'status-default';
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="page-header-quotation">
          <div className="container">
            <div className="header-content">
              <div>
                <h1 className="page-title">Quotations</h1>
                <p className="page-subtitle">Manage customer quotations</p>
              </div>
              <button 
                onClick={() => navigate('/quotations/new')} 
                className="btn btn-primary"
              >
                + New Quotation
              </button>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="quotation-search-bar">
            <input
              type="text"
              placeholder="🔍 Search quotation no / mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="quotation-search-input"
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner large"></div>
              <p>Loading quotations...</p>
            </div>
          ) : quotations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <div className="empty-text">No quotations found</div>
              <div className="empty-subtext">
                {search ? 'Try a different search term' : 'Create your first quotation'}
              </div>
              {!search && (
                <button 
                  onClick={() => navigate('/quotations/new')} 
                  className="btn btn-primary"
                  style={{ marginTop: '20px' }}
                >
                  + Create Quotation
                </button>
              )}
            </div>
          ) : (
            <div className="quotation-grid">
              {quotations.map((q) => (
                <div 
                  key={q.id} 
                  className="quotation-card"
                  onClick={() => navigate(`/quotations/${q.id}`)}
                >
                  <div className="quotation-card-header">
                    <div>
                      <h3 className="quotation-number">{q.quote_no}</h3>
                      <span className="quotation-version">v{q.version}</span>
                    </div>
                    <span className={`status-badge ${getStatusBadgeClass(q.status)}`}>
                      {q.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="quotation-card-body">
                    <div className="quotation-customer">
                      <span className="customer-icon">👤</span>
                      <div>
                        <div className="customer-name">
                          {q.customer?.name || q.customer_name || 'Customer'}
                        </div>
                        <div className="customer-mobile">
                          {q.customer?.mobile || q.customer_mobile || '-'}
                        </div>
                      </div>
                    </div>

                    <div className="quotation-total">
                      <span className="total-label">Total Amount</span>
                      <span className="total-amount">₹{Math.round(normalizeNumber(q.total))}</span>
                    </div>
                  </div>

                  <div className="quotation-card-footer">
                    <span className="quotation-date">
                      {new Date(q.created_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                    {q.status !== 'converted' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteQuotation(q.id);
                        }}
                        className="btn-icon-delete"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationList;